import { FC, useState } from "react";
import { Modal } from "components/modal";
import { Button } from "components/button";
import { InputField } from "components/fields/InputField";
import { PAYMENT_METHOD, InvoiceTransaction } from "modules/invoice/types/invoice.interface";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddPayment: (payment: Omit<InvoiceTransaction, "_id" | "createdAt" | "createdBy">) => void;
  invoiceTotal: number;
  totalPaid: number;
}

export const PaymentModal: FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onAddPayment,
  invoiceTotal,
  totalPaid,
}) => {
  const [payment, setPayment] = useState<Omit<InvoiceTransaction, "_id" | "createdAt" | "createdBy">>({
    amount: invoiceTotal - totalPaid,
    paymentDate: new Date(),
    paymentMethod: PAYMENT_METHOD.BANK_TRANSFER,
    reference: "",
    notes: "",
  });

  const handleSubmit = () => {
    onAddPayment(payment);
    onClose();
  };

  if (!isOpen) return null;

  const remainingBalance = invoiceTotal - totalPaid;

  return (
    <Modal>
      <div className="rounded-xl bg-white p-6 shadow-xl w-full min-w-[275px] sm:w-[500px]">
        <h2 className="mb-4 text-xl font-bold">Ajouter un paiement</h2>
        
        <div className="mb-5 grid grid-cols-2 gap-4">
          <div className="text-sm font-medium text-gray-600">Total facture</div>
          <div className="text-right text-sm">{invoiceTotal.toFixed(2)} €</div>
          
          <div className="text-sm font-medium text-gray-600">Déjà payé</div>
          <div className="text-right text-sm">{totalPaid.toFixed(2)} €</div>
          
          <div className="text-sm font-medium text-gray-600">Reste à payer</div>
          <div className="text-right text-sm font-semibold">{remainingBalance.toFixed(2)} €</div>
        </div>

        <div className="mb-4">
          <InputField
            variant="auth"
            label="Montant"
            id="payment-amount"
            type="number"
            extra="mb-3"
            placeholder="0.00"
            value={payment.amount}
            onChange={(e) => setPayment({ ...payment, amount: parseFloat(e.target.value) || 0 })}
          />
        </div>

        <div className="mb-4">
          <InputField
            variant="auth"
            placeholder="Date de paiement"
            label="Date de paiement"
            id="payment-date"
            type="date"
            extra="mb-3"
            value={payment.paymentDate instanceof Date ? payment.paymentDate.toISOString().split("T")[0] : new Date().toISOString().split("T")[0]}
            onChange={(e) => setPayment({ ...payment, paymentDate: e.target.value ? new Date(e.target.value) : new Date() })}
          />
        </div>

        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-navy-700">Méthode de paiement</label>
          <select
            className="w-full rounded-xl border border-gray-300 p-3 text-sm outline-none focus:border-brand-500"
            value={payment.paymentMethod}
            onChange={(e) => setPayment({ ...payment, paymentMethod: e.target.value as PAYMENT_METHOD })}
          >
            <option value={PAYMENT_METHOD.BANK_TRANSFER}>Virement bancaire</option>
            <option value={PAYMENT_METHOD.CREDIT_CARD}>Carte bancaire</option>
            <option value={PAYMENT_METHOD.CHECK}>Chèque</option>
            <option value={PAYMENT_METHOD.CASH}>Espèces</option>
            <option value={PAYMENT_METHOD.OTHER}>Autre</option>
          </select>
        </div>

        <div className="mb-4">
          <InputField
            variant="auth"
            label="Référence (optionnel)"
            id="payment-reference"
            type="text"
            extra="mb-3"
            placeholder="Numéro de transaction, chèque, etc."
            value={payment.reference || ""}
            onChange={(e) => setPayment({ ...payment, reference: e.target.value })}
          />
        </div>

        <div className="mb-4">
          <InputField
            variant="auth"
            label="Notes (optionnel)"
            id="payment-notes"
            type="text"
            extra="mb-3"
            placeholder="Notes additionnelles"
            value={payment.notes || ""}
            onChange={(e) => setPayment({ ...payment, notes: e.target.value })}
          />
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button onClick={onClose} variant="alert">Annuler</Button>
          <Button onClick={handleSubmit} variant="primary" disabled={payment.amount <= 0 || payment.amount > remainingBalance}>
            Enregistrer le paiement
          </Button>
        </div>
      </div>
    </Modal>
  );
}; 